<script setup lang="ts">
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import DeveloperControls from './components/DeveloperControls.vue'
import FundsFlowCanvas, { type PlaybackStatus } from './components/FundsFlowCanvas.vue'
import { cloneVisualizationConfig, realVisualizationConfig } from './config/visualization.config'
import { marketDataProvider } from './data'
import { loadLatestRealSnapshot, loadRealSnapshot } from './data/RealSnapshotMarketDataProvider'
import { selectFixedSectors, selectRealFixedSectors } from './data/selectFixedSectors'
import type { MarketFlowData } from './types/market'
import { createRenderContext, parseRenderMode, type RenderContext } from './render/RenderContext'
import {
  IncompleteVideoDataError,
  VideoGenerationService,
  type VideoGenerationContext,
  type VideoJob,
  type VideoJobMode,
} from './services/VideoGenerationService'

const data = ref<MarketFlowData>()
const renderContext = ref<RenderContext>()
const error = ref('')
const ready = ref(false)
const videoJob = ref<VideoJob | null>(null)
const videoError = ref('')
const canvasComponent = ref<InstanceType<typeof FundsFlowCanvas>>()
const config = reactive(cloneVisualizationConfig())
const videoGenerationService = new VideoGenerationService()
let videoPollTimer = 0
const query = new URLSearchParams(window.location.search)
const isExportMode = query.get('export') === '1'
const dataSource = query.get('source')?.toLowerCase() === 'mock' ? 'MOCK' : 'REAL'
if (dataSource === 'REAL') Object.assign(config, realVisualizationConfig)
if (query.get('physicsDebug') === '1') config.physicsDebug = true
const renderMode = parseRenderMode(query.get('renderMode'))
const queryMinuteDuration = Number(query.get('marketMinuteDuration'))
const queryFps = Number(query.get('fps'))
if (Number.isFinite(queryMinuteDuration) && queryMinuteDuration >= 50 && queryMinuteDuration <= 2000) {
  config.marketMinuteDuration = queryMinuteDuration
}
if (Number.isFinite(queryFps) && queryFps >= 24 && queryFps <= 120) {
  config.fps = queryFps
  config.frameDuration = 1000 / queryFps
}
if (!Number.isFinite(queryMinuteDuration)) {
  config.marketMinuteDuration = renderMode === 'noon'
    ? config.noonMarketMinuteDuration
    : renderMode === 'close'
      ? config.closeMarketMinuteDuration
      : config.marketMinuteDuration
}
const status = reactive<PlaybackStatus>({
  playing: false,
  elapsedMs: 0,
  durationMs: 0,
  speed: 1,
  frameIndex: 0,
  time: '09:30',
})

function updateStatus(next: PlaybackStatus): void {
  Object.assign(status, next)
}

async function pollVideoJob(jobId: string): Promise<void> {
  try {
    const next = await videoGenerationService.getJob(jobId)
    videoJob.value = next
    if (next.status === 'failed') {
      videoError.value = next.error || '视频生成失败'
      return
    }
    if (next.status === 'completed') return
    videoPollTimer = window.setTimeout(() => void pollVideoJob(jobId), 500)
  } catch (reason) {
    videoError.value = reason instanceof Error ? reason.message : '无法读取视频生成状态'
  }
}

async function startVideoGeneration(
  mode: VideoJobMode,
  targetDurationSeconds: number,
  allowIncomplete = false,
): Promise<void> {
  videoError.value = ''
  try {
    if (!data.value?.date) throw new Error('当前页面没有可用于视频生成的交易日期')
    const generationContext: VideoGenerationContext = { dataSource, date: data.value.date }
    const job = mode === 'noon'
      ? await videoGenerationService.generateNoonVideo(generationContext, targetDurationSeconds, allowIncomplete)
      : await videoGenerationService.generateCloseVideo(generationContext, targetDurationSeconds, allowIncomplete)
    videoJob.value = job
    window.clearTimeout(videoPollTimer)
    await pollVideoJob(job.id)
  } catch (reason) {
    if (reason instanceof IncompleteVideoDataError) {
      const proceed = window.confirm(`${reason.report.warning}\n\n默认建议取消。是否仍然使用现有数据生成？`)
      if (proceed) await startVideoGeneration(mode, targetDurationSeconds, true)
      return
    }
    videoError.value = reason instanceof Error ? reason.message : '视频生成失败'
  }
}

async function openOutputDirectory(): Promise<void> {
  try {
    await videoGenerationService.openOutputDirectory()
  } catch (reason) {
    videoError.value = reason instanceof Error ? reason.message : '无法打开输出目录'
  }
}

function playGeneratedVideo(): void {
  if (!videoJob.value) return
  window.open(videoGenerationService.getPlaybackUrl(videoJob.value.id), '_blank', 'noopener')
}

onMounted(async () => {
  try {
    const realDate = query.get('date') ?? ''
    const realMinute = query.get('minute') ?? ''
    let source: MarketFlowData
    let activeRenderMode = renderMode
    if (dataSource === 'REAL') {
      const real = realDate && realMinute
        ? await loadRealSnapshot(realDate, realMinute)
        : await loadLatestRealSnapshot()
      source = real.data
      activeRenderMode = parseRenderMode(real.mode)
    } else {
      source = await marketDataProvider.load()
    }
    const context = createRenderContext(activeRenderMode, source, config)
    context.data = dataSource === 'REAL'
      ? selectRealFixedSectors(context.data)
      : selectFixedSectors(context.data, 45)
    if (dataSource === 'REAL') context.title = `REAL · ${context.title}`
    else if (!isExportMode) context.title = `MOCK · ${context.title}`
    renderContext.value = context
    data.value = context.data
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '行情数据加载失败'
  }
})

onBeforeUnmount(() => window.clearTimeout(videoPollTimer))
</script>

<template>
  <main class="app-shell">
    <FundsFlowCanvas
      v-if="data && renderContext"
      ref="canvasComponent"
      :data="data"
      :config="config"
      :render-context="renderContext"
      @ready="ready = true"
      @status="updateStatus"
    />
    <div v-else-if="error" class="state-card state-card--error">
      <strong>无法启动资金流向引擎</strong>
      <span>{{ error }}</span>
    </div>
    <div v-else class="state-card">
      <span class="loader" />
      <strong>正在装载分钟级资金场</strong>
    </div>

    <DeveloperControls
      v-if="data && ready && !isExportMode"
      :data="data"
      :config="config"
      :status="status"
      :video-job="videoJob"
      :video-error="videoError"
      @play="canvasComponent?.play()"
      @pause="canvasComponent?.pause()"
      @restart="canvasComponent?.restart()"
      @previous="canvasComponent?.previousFrame()"
      @next="canvasComponent?.nextFrame()"
      @seek="canvasComponent?.seekMs($event)"
      @seek-frame="canvasComponent?.seekFrame($event)"
      @speed="canvasComponent?.setSpeed($event)"
      @generate-video="startVideoGeneration"
      @open-output="openOutputDirectory"
      @play-video="playGeneratedVideo"
    />
  </main>
</template>
